/* eslint-disable import/no-anonymous-default-export */
import React, { useState } from 'react';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { FilledSquareIcon, EmptySquareIcon } from 'global/components/PerformIcons';
import { getColorFromScore } from 'global/styles';
import { BARHEIGHT, COLORS, CompareModal, CompareWrap, ScoreIconWrap, SCORE_ICON_DISABLED_CLASS } from './styles';
import { useView } from 'global/hooks/useView';
import { get } from '../../lib/lodash';
import clsx from 'clsx';
import { EditScore } from '../../../feature/report/ChecklistScores';
import { lowerCaseAll } from '../../utils';
import { Text } from '../text';
import { Checkbox } from '../checkbox';
import { useScreenDims } from '../../styles';
import { useHashView } from '../../../feature/share/useHashView';

const shouldSwitch = (v) => {
  return ['goal', 'targetGoal'].includes(v);
};

export const displayText = (score, compare, opts = {}) => {
  const compareval = get(compare, 'value');
  const _score = score.value;
  const scoreIsNull = _score == null || _score == -1 || _score == undefined;
  const compareIsNull = compareval == null || compareval == -1 || compareval == undefined;

  const switchCompare = shouldSwitch(get(opts, 'filter.assessment_status'));

  // If no good comparison, the active score displays itself, whether N/A, N/O or otherwise.
  const compareDisplay = get(compare, 'displayValue');
  const scoreDisplay = get(score, 'displayValue', get(opts, 'pssvCheckpoint.displayValue'));
  if (compareIsNull || scoreIsNull) {
    return <em>{switchCompare ? compareDisplay || scoreDisplay : scoreDisplay || compareDisplay}</em>;
  }

  const diff = switchCompare ? compareval - _score : _score - compareval;
  const config = get(opts, 'scorecard.config', {});

  let disp = config.displayPercentage ? diff : diff * ((config.maxScore - config.minScore) / 100);

  if (isNaN(disp)) {
    return '';
  }

  disp = `${disp}`.length > 3 ? disp.toFixed(2) : disp;

  const pref = disp >= 0 ? '+' : '';
  const suff = config.displayPercentage ? '%' : '';

  return `${pref}${disp}${suff}`;
};

const cont = { width: 466, height: 558 };

const BarCompare = (props) => {
  const { score, compare } = props;
  const [state, setState] = useState({
    anchor: null,
  });

  const switchCompare = shouldSwitch(get(props, 'filter.assessment_status'));
  const { view, getView, renderConfirm, confirmState, setView } = useView();
  const { width, height } = useScreenDims();

  // Score refers to the Active value (unless swapped) -- which is the value towards which the diagram grows/shrinks
  const _score = switchCompare ? get(compare, 'value', score) : score; // Set compare to score where undefined specifically to ensure that when creating new goal scores, it displays like the existing when non-existant
  // Compare refers to the Display the user selected like Target / Draft / Review (unless Goal, where it's swapped)
  //.  which is the value the diagram positions as the baseline, the black vertical line
  const compareval = switchCompare ? score : get(compare, 'value');

  const scoreIsNull = _score == null || _score == -1 || _score == undefined;
  const compareIsNull = compareval == null || compareval == -1 || compareval == undefined;

  let filtstatus = `${get(props, 'filter.assessment_status')}`;
  const isTargetGoal = filtstatus == 'targetGoal';

  if (isTargetGoal) {
    filtstatus = 'goal';
  }

  const blackBar = () => {
    return { style: { width: 2, height: BARHEIGHT + 8, backgroundColor: 'black', top: -4 } };
  };

  const generateBars = () => {
    // If there's no baseline:
    if (compareIsNull) {
      // If also no active value, it's just a empty box
      // otherwise, we show a dark grey bar representing the active value (or goal), and no blackBar
      return scoreIsNull ? [] : [{ style: { width: `${_score}%`, backgroundColor: COLORS.darkGrey } }];
    }

    // If there's nothing to compare the baseline to:
    if (scoreIsNull) {
      // we show that baseline as a vertical bar with no background
      return [{ style: { width: `${compareval}%`, backgroundColor: 'transparent' } }, blackBar()];
    }

    // Else we show both the baseline and the difference
    const val = _score - compareval;
    return [
      { style: { width: `${compareval}%`, backgroundColor: COLORS.lightGray } },
      blackBar(),
      { style: { width: `${Math.abs(val)}%`, marginLeft: val < 0 ? `calc(${val}% - 2px)` : 0, backgroundColor: val > 0 ? COLORS.green : COLORS.red } },
    ].filter((f) => !!f);
  };

  const canClick = get(props, 'canClick', true);
  const onClick = (e) => {
    // console.log({ compare, compareval, props, score, val: _score - compareval, switchCompare })
    if (!canClick) {
      return; // props.onClick && props.onClick(e)
    }
    const elem = document.getElementById('ActiveAbsolute');
    setState((s) => ({ ...s, anchor: elem }));
  };

  const onClose = (afterSubmit = false) => {
    setState((s) => ({ ...s, anchor: null }));

    const statCheck = ['goal'].includes(filtstatus);

    const openTask = () => {
      props.openTaskCreate();
    };

    // PFM-2700: don't prompt to create a task when every allowed task type for this
    // checkpoint already has a task. The triple-dot menu still lists Create in that
    // case, disabled. Suppress the prompt only when task types exist but none remain.
    // When no task types are configured at all (availableTaskTypeCount === 0), preserve
    // the generic "Task" prompt so nothing regresses.
    const availableTaskTypeCount = get(props, 'availableTaskTypeCount', 0);
    const remainingTaskTypeCount = get(props, 'remainingTaskTypeCount', 0);
    const noTaskTypesRemain = availableTaskTypeCount > 0 && remainingTaskTypeCount === 0;
    // Parents with instances aren't a task target — tasks are created on the instances.
    const suppressCreateTask = !!props.suppressCreateTask;

    if (afterSubmit && statCheck && !suppressCreateTask && !noTaskTypesRemain && !props.isTemplate && get(props, 'assessment.data.meta.tasksEnabled') !== false) {
      const dontShow = getView().dontShowTaskOnScore;

      const updateConf = () => {
        const closeConf = () => confirmState[1]({});
        const v = getView();

        const typeLabel = props.taskTypeName || 'Task';
        confirmState[1]({
          title: `Create ${typeLabel}`,
          content: (
            <div>
              <Text>Would you like to create a {typeLabel.toLowerCase()} for this target?</Text>
              <Checkbox
                label="Don't show me this again."
                checked={v.dontShowTaskOnScore}
                onClick={() => {
                  setView('dontShowTaskOnScore', !v.dontShowTaskOnScore);
                  updateConf();
                }}
              />
              <Text italic>You can create a {typeLabel.toLowerCase()} later by using the dropdown next to this score.</Text>
            </div>
          ),
          okayText: `Start ${typeLabel}`,
          cancelText: 'No',
          onOk: () => {
            setView('createTaskOnScore', true);
            closeConf();

            openTask();
          },
          onCancel: () => {
            if (v.dontShowTaskOnScore) {
              setView('createTaskOnScore', false);
            }
            closeConf();
          },
        });
      };

      if (dontShow) {
        if (getView().createTaskOnScore) {
          openTask();
        }
      } else if (getView().createTaskOnScore) {
        updateConf();
      }
    }
  };

  const onUpdate = (v, fullUp, resp) => {
    return get(props, 'scores.update', () => 0)({ loading: false, resp, compare });
  };

  const addToQueue = async (d) => {
    props.addToQueue && (await props.addToQueue(d, { onUpdate }));

    const filtStat = get(props, 'filter.assessment_status');
    if (!get(props, 'assessment.data.availableStatus', []).includes(filtStat)) {
      await get(props, 'assessment.update', () => 0)({ loading: false });
    }

    onClose(true);
  };

  // PFM-2547: hashed share-link viewers see scores read-only — even when the
  // assessment's own meta.targetsEnabled is true, no edits are allowed and the
  // popover must not offer "set new target" controls.
  const { active: hashViewActive } = useHashView();

  const hasScores = get(props, 'assessment.data.availableStatus', []).includes(filtstatus);
  // PFM-2701: New Targets created before the "Update New Targets" toggle was
  // turned off must become read-only (still viewable). Treat targets as enabled
  // unless explicitly false so older assessments without the meta key stay editable.
  const targetsEnabled = get(props, 'assessment.data.meta.targetsEnabled') !== false;
  // Distinguish "this is the New Target modal" (display) from "the target can be
  // edited" (permission). When targetsEnabled is off, the existing target must
  // still be shown read-only, so display gating uses isGoalModal while editing
  // gating continues to use canScore.
  const isGoalModal = filtstatus === 'goal' && !hashViewActive;
  const canScore = isGoalModal && targetsEnabled;
  const compareVal = get(props, `pssvCheckpoint.${['goal', 'target'].includes(filtstatus) ? 'target' : 'compare'}.value`);
  const activeVal = get(props, 'pssvCheckpoint.value');
  const isTemplate = !!props.isTemplate;
  const tasksEnabled = get(props, 'assessment.data.meta.tasksEnabled') !== false && !hashViewActive;

  return (
    <>
      <CompareWrap onClick={onClick} data-testid="assessment-score-compare-target" className={clsx(canClick && !Boolean(state.anchor) && 'compareHover')}>
        <div className={'barContainer'}>
          {generateBars().map((b, i) => (
            <div key={i} className="bar" style={b?.style} />
          ))}
        </div>

        <div className="text">{displayText({ ...(props.active || {}), value: score }, compare, { ...props })}</div>
      </CompareWrap>
      {Boolean(state.anchor) && (
        <CompareModal
          id="scoreMenu"
          anchorEl={state.anchor}
          open={Boolean(state.anchor)}
          onClose={() => onClose()}
          anchorPosition={{ left: width * 0.5 - cont.width * 0.5, top: height * 0.5 - cont.height * 0.5 }}
          anchorReference="anchorPosition"
        >
          <div className={clsx('container', !canScore && 'noScore')}>
            <div className="header">
              <div>{`${canScore ? 'SET ' : ''}${isTargetGoal || filtstatus === 'goal' ? 'NEW TARGET' : lowerCaseAll(filtstatus).toUpperCase()} SCORE`}</div>
            </div>
            {!!canScore && <Text variant="caption">{_score ? `The highlighted option is your new target;` : `No new target is set;`} click options to change it.</Text>}
            <div className="content">
              <EditScore
                hideRightGrid={true}
                setTargetStatus={filtstatus}
                highlightActive={activeVal ?? undefined}
                highlightCompare={compareVal ?? undefined}
                {...props}
                addToQueue={addToQueue}
                canScore={canScore}
                isCompareModal
                alwaysSetComment
                pssvCheckpoint={{
                  ...(props.compare || props.pssvCheckpoint),
                  // Show the existing target highlighted whenever this is the New
                  // Target modal — even when targetsEnabled is off (read-only view).
                  value: isGoalModal && hasScores ? _score : null,
                }}
              />
            </div>

            {canScore && !isTemplate && tasksEnabled && !props.suppressCreateTask && (
              <div>
                <Checkbox data-testid="assessment-scores-score-checkbox" label="Create Tasks" checked={view.createTaskOnScore} onClick={() => setView('createTaskOnScore', !view.createTaskOnScore)} />
              </div>
            )}
          </div>
        </CompareModal>
      )}

      {renderConfirm()}
    </>
  );
};

// Expects props.score as a float between 0 and 1
// score values -1 are processed to black
export default (props) => {
  const { isBw, isAddMenu } = props;
  const { isDark } = useView();

  const useCompare = get(props, 'useCompare', false);

  if (useCompare) {
    return <BarCompare {...props} />;
  }

  if (isAddMenu) {
    if (props.score == null) {
      return <AddCircleOutlineIcon style={{ color: COLORS.darkGrey }} />;
    }
    return <AddCircleIcon style={{ color: isDark ? 'white' : 'black' }} />;
  } else if (props.score === null) {
    return <EmptySquareIcon roundedInner={props.bordered} color="disabled" style={{ paddingRight: '4px' }} />;
  } else if (props.selected) {
    return (
        <ScoreIconWrap className={clsx(props.disabled && SCORE_ICON_DISABLED_CLASS)} onClick={props.onClick}>
        <CheckBoxIcon style={{ color: isDark ? 'white' : 'black' }} />
      </ScoreIconWrap>
    );
  } else if (props.indeterminate) {
    return (
        <ScoreIconWrap className={clsx(props.disabled && SCORE_ICON_DISABLED_CLASS)} onClick={props.onClick}>
        <IndeterminateCheckBoxIcon style={{ color: isDark ? 'white' : 'black' }} />
      </ScoreIconWrap>
    );
  }

  const sc = typeof props.score === 'string' ? parseFloat(props.score) : props.score;
  const hasRanges = props.scorecardConfig ? [get(props, 'scorecardConfig.minScore', 0), get(props, 'scorecardConfig.maxScore', 100)] : undefined;
  const fillColor = getColorFromScore(sc, hasRanges, isBw);

  if (!props.bordered) {
    const stroke = props.useBlankCheckbox ? (isDark ? 'white' : 'black') : undefined;
    const opacity = props.useBlankCheckbox ? 0.4 : 1;
    return (
        <ScoreIconWrap className={clsx(props.disabled && SCORE_ICON_DISABLED_CLASS)} onClick={props.onClick}>
        <FilledSquareIcon
          style={{
            color: fillColor,
            stroke,
            fillOpacity: opacity,
            strokeWidth: stroke ? 2 : undefined,
          }}
        />
      </ScoreIconWrap>
    );
  }

  const inSelectMode = props.useBlankCheckbox;
  const selectFade = inSelectMode ? 0.4 : 1;
  const selectStroke = inSelectMode && props.actionable && !props.disabled;
  const showRing = !props.disabled && !selectStroke;
  const blackRing = showRing && props.actionable && !inSelectMode;

  return (
        <ScoreIconWrap className={clsx(props.disabled && SCORE_ICON_DISABLED_CLASS)} onClick={props.onClick}>
      <FilledSquareIcon
        bordered={showRing}
        ringColor={blackRing ? (isDark ? 'white' : 'black') : undefined}
        innerFillOpacity={showRing && inSelectMode ? selectFade : 1}
        style={{
          color: fillColor,
          ...(selectStroke && {
            stroke: isDark ? 'white' : 'black',
            fillOpacity: selectFade,
            strokeWidth: 2,
          }),
        }}
      />
    </ScoreIconWrap>
  );
};
